/**
 * The only place this application talks to `core-api`.
 *
 * Three things are true here and nowhere else:
 *
 * 1. **No token is ever handled.** Authentication is two httpOnly cookies; the
 *    client sends `credentials: "same-origin"` and knows nothing more. That is
 *    what makes a 90-day session on a family tablet defensible.
 * 2. **Every call is typed from the generated contract** (`schema.ts`). A field
 *    renamed server-side fails `npm run build`, not the tablet (SPEC §13).
 * 3. **The child shell cannot reach a route naming a child** — checked below,
 *    at run time, on the built URL (invariant 2).
 */

import { ApiError, PartitionViolation, offlineError } from "@/api/errors";
import type { OperationName, Operations } from "@/api/schema";
import { demarrer, terminer } from "@/api/chargement";

/** Which shell is driving. Set once, at sign-in, by the shell router. */
export type Shell = "child" | "parent" | "unknown";

let currentShell: Shell = "unknown";

export function setShell(shell: Shell): void {
  currentShell = shell;
}

export function getShell(): Shell {
  return currentShell;
}

/**
 * A URL a child must never build.
 *
 * The rule is `core-api` §5.2 read from the outside: a child names no one, not
 * even themselves. `/api/auth/me` is the single place their own identity comes
 * from, and it takes no parameter.
 */
export function violatesChildPartition(url: string): boolean {
  return (
    /\/api\/children\b/.test(url) ||
    /\/api\/audit\b/.test(url) ||
    /\/api\/auth-sessions\b/.test(url) ||
    // La V2 parentale nomme un enfant dans son chemin, sous un autre mot que
    // `children` : l'ajouter ici est la moitié client de la règle, la moitié
    // serveur étant le refus de la session enfant sur ces routes.
    /\/api\/v2\/enfants\b/.test(url)
  );
}

type Params<N extends OperationName> = Operations[N]["params"];
type Query<N extends OperationName> = Operations[N]["query"];
type Body<N extends OperationName> = Operations[N]["body"];

export type CallOptions<N extends OperationName> = {
  params?: Params<N>;
  query?: Query<N>;
  body?: Body<N>;
  signal?: AbortSignal;
};

const OPERATIONS: { [N in OperationName]: { method: string; path: string } } = {
  parentLogin: { method: "POST", path: "/api/auth/parent/login" },
  childLogin: { method: "POST", path: "/api/auth/child/login" },
  deviceOwner: { method: "GET", path: "/api/auth/device" },
  logout: { method: "POST", path: "/api/auth/logout" },
  me: { method: "GET", path: "/api/auth/me" },
  planDeRevision: { method: "GET", path: "/api/exercises/revision" },
  exerciceDeRevision: { method: "POST", path: "/api/exercises/revision/next" },
  myProgression: { method: "GET", path: "/api/auth/me/progression" },
  maSemaine: { method: "GET", path: "/api/auth/me/semaine" },
  maCarte: { method: "GET", path: "/api/auth/me/carte" },
  mesEcheances: { method: "GET", path: "/api/auth/me/echeances" },
  maJournee: { method: "GET", path: "/api/auth/me/journee" },
  monJournal: { method: "GET", path: "/api/auth/me/journal" },
  enregistrerMonJournal: { method: "PUT", path: "/api/auth/me/journal" },
  historiqueDeMonJournal: { method: "GET", path: "/api/auth/me/journal/historique" },
  mesJournauxOublies: { method: "GET", path: "/api/auth/me/journal/oublies" },
  monJournalOublie: { method: "GET", path: "/api/auth/me/journal/rattrapage/{le}" },
  enregistrerMonJournalOublie: {
    method: "PUT",
    path: "/api/auth/me/journal/rattrapage/{le}",
  },
  mesDevoirs: { method: "GET", path: "/api/auth/me/devoirs" },
  photographierUnDevoir: { method: "POST", path: "/api/auth/me/devoirs/photo" },
  retirerUnePhoto: { method: "DELETE", path: "/api/auth/me/devoirs/photo/{photo_id}" },
  mesReglages: { method: "GET", path: "/api/auth/me/reglages" },
  choisirMonCap: { method: "PUT", path: "/api/auth/me/reglages" },
  travailDeLaJournee: { method: "GET", path: "/api/exercises/du-jour" },
  travailDuneMatiere: { method: "GET", path: "/api/exercises/du-jour/matiere/{matiere}" },
  exerciceDuJour: { method: "POST", path: "/api/exercises/du-jour/next" },
  serieFlash: { method: "POST", path: "/api/exercises/flash/serie" },
  positionnementEtat: { method: "GET", path: "/api/exercises/positionnement" },
  positionnementSuivant: {
    method: "POST",
    path: "/api/exercises/positionnement/{matiere}/suivant",
  },
  listChildren: { method: "GET", path: "/api/children" },
  dashboard: { method: "GET", path: "/api/children/{child_id}/dashboard" },
  effort: { method: "GET", path: "/api/children/{child_id}/effort" },
  cahierDeTravail: { method: "GET", path: "/api/children/{child_id}/travail" },
  travailPrevu: { method: "GET", path: "/api/children/{child_id}/prochain" },
  cahierDesProfs: { method: "GET", path: "/api/children/{child_id}/classe" },
  travailDemande: { method: "GET", path: "/api/children/{child_id}/travail-demande" },
  vuEnClasseEtat: { method: "GET", path: "/api/children/{child_id}/vu-en-classe" },
  vuEnClasseDeclarer: { method: "POST", path: "/api/children/{child_id}/vu-en-classe" },
  vuEnClasseConseil: {
    method: "POST",
    path: "/api/children/{child_id}/vu-en-classe/conseil",
  },
  summaries: { method: "GET", path: "/api/children/{child_id}/summaries" },
  journalDeLEnfant: { method: "GET", path: "/api/children/{child_id}/journal" },
  requestExport: { method: "POST", path: "/api/children/{child_id}/export" },
  exportStatus: { method: "GET", path: "/api/children/{child_id}/export/{job_id}" },
  requestDeletion: { method: "DELETE", path: "/api/children/{child_id}" },
  cancelDeletion: { method: "POST", path: "/api/children/{child_id}/deletion/cancel" },
  enrolDevice: { method: "POST", path: "/api/children/{child_id}/devices" },
  listDevices: { method: "GET", path: "/api/children/{child_id}/devices" },
  revokeDevice: { method: "DELETE", path: "/api/devices/{device_id}" },
  listSessions: { method: "GET", path: "/api/auth-sessions" },
  revokeSession: { method: "POST", path: "/api/auth-sessions/{session_id}/revoke" },
  readAudit: { method: "GET", path: "/api/audit" },
  poserAuChat: { method: "POST", path: "/api/parents/chat" },
  filsDeChat: { method: "GET", path: "/api/parents/chat" },
  filDeChat: { method: "GET", path: "/api/parents/chat/{conversation_id}" },
  etatDuCanalTelegram: { method: "GET", path: "/api/parents/telegram" },
  emettreLeLienTelegram: { method: "POST", path: "/api/parents/telegram/lien" },
  envoyerUnTestTelegram: { method: "POST", path: "/api/parents/telegram/test" },
  openSession: { method: "POST", path: "/api/sessions" },
  postMessage: { method: "POST", path: "/api/sessions/{tutor_session_id}/messages" },
  endSession: { method: "POST", path: "/api/sessions/{tutor_session_id}/end" },
  streamEvents: { method: "GET", path: "/api/sessions/{tutor_session_id}/events" },
  nextExercise: { method: "GET", path: "/api/exercises/next" },
  submitAttempt: { method: "POST", path: "/api/exercises/{exercise_id}/attempt" },
  demanderUneValidation: {
    method: "POST",
    path: "/api/exercises/{exercise_id}/validation",
  },
  pasEncoreVu: {
    method: "POST",
    path: "/api/exercises/{exercise_id}/pas-encore-vu",
  },
  fileDeValidation: { method: "GET", path: "/api/validations" },
  valider: { method: "POST", path: "/api/validations/{request_id}" },
  juger: { method: "POST", path: "/api/validations/{request_id}/verdict" },
  presign: { method: "POST", path: "/api/photos/presign" },
  parascolaireOuvrages: { method: "GET", path: "/api/v2/enfants/{child_id}/ouvrages" },
  parascolaireChapitres: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/ouvrages/{ouvrage}/chapitres",
  },
  parascolaireChapitre: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/ouvrages/{ouvrage}/chapitres/{cle}",
  },
  composerUnEntrainement: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/ouvrages/{ouvrage}/chapitres/{cle}/entrainements",
  },
  proposerUnEntrainement: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/entrainements/proposition",
  },
  tirerUnEntrainement: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/entrainements/tirage",
  },
  genererUnEntrainement: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/entrainements/generer",
  },
  cloreLEntrainement: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/entrainements/clore",
  },
  retirerUneLigneDEntrainement: {
    method: "DELETE",
    path: "/api/v2/enfants/{child_id}/entrainements/lignes/{ligne_id}",
  },
  entrainementsDeLEnfant: { method: "GET", path: "/api/v2/enfants/{child_id}/entrainements" },
  // Les deux PDF ne passent pas par `call` : ce sont des liens que le
  // navigateur ouvre lui-même, avec le cookie de session. `buildUrl` suffit,
  // et c'est ce qui laisse le lecteur du navigateur imprimer.
  imprimerLaFeuille: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/entrainements/{entrainement_id}/feuille.pdf",
  },
  imprimerLeCorrige: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/entrainements/{entrainement_id}/corrige.pdf",
  },
  // Même principe pour un sujet entier ou un exercice à images d'énoncé : des
  // routes du parent seulement, aucune en `/me` (règle 2).
  imprimerLEnonce: {
    method: "GET",
    path: "/api/children/{child_id}/exercices/{exercise_id}/sujet.pdf",
  },
  imprimerLeCorrigeDeLExercice: {
    method: "GET",
    path: "/api/children/{child_id}/exercices/{exercise_id}/corrige.pdf",
  },
  imprimerLeSujet: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/sujets/{sujet_donne_id}/sujet.pdf",
  },
  imprimerLeCorrigeDuSujet: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/sujets/{sujet_donne_id}/corrige.pdf",
  },
  mesEntrainements: { method: "GET", path: "/api/v2/me/entrainements" },
  photographierUnEntrainement: {
    method: "PUT",
    path: "/api/v2/me/entrainements/lignes/{ligne_id}/photo",
  },
  retirerLaPhotoDEntrainement: {
    method: "DELETE",
    path: "/api/v2/me/entrainements/lignes/{ligne_id}/photo",
  },
  // Les entraînements que l'élève compose lui-même (Hedi, 5 octobre 2026).
  mesOuvrages: { method: "GET", path: "/api/v2/me/ouvrages" },
  mesChapitres: { method: "GET", path: "/api/v2/me/ouvrages/{ouvrage}/chapitres" },
  monChapitre: { method: "GET", path: "/api/v2/me/ouvrages/{ouvrage}/chapitres/{cle}" },
  composerMonEntrainement: {
    method: "POST",
    path: "/api/v2/me/ouvrages/{ouvrage}/chapitres/{cle}/entrainements",
  },
  proposerMonEntrainement: { method: "POST", path: "/api/v2/me/entrainements/proposition" },
  tirerMonEntrainement: { method: "POST", path: "/api/v2/me/entrainements/tirage" },
  genererMonEntrainement: { method: "POST", path: "/api/v2/me/entrainements/generer" },
  retirerUneLigneDeMonEntrainement: {
    method: "DELETE",
    path: "/api/v2/me/entrainements/lignes/{ligne_id}",
  },
  cloreMonEntrainement: { method: "POST", path: "/api/v2/me/entrainements/clore" },
  mesPropresEntrainements: { method: "GET", path: "/api/v2/me/entrainements/les-miens" },
  entrainementsAJuger: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/entrainements/a-juger",
  },
  jugerUnEntrainement: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/entrainements/lignes/{ligne_id}/verdict",
  },
  ouvrirUneSeanceADeux: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/seances-a-deux",
  },
  lireUneSeanceADeux: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/seances-a-deux/{seance_id}",
  },
  devoilerUnIndice: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/seances-a-deux/{seance_id}/indice",
  },
  declarerUneTentative: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/seances-a-deux/{seance_id}/tentative",
  },
  jugerUneSeanceADeux: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/seances-a-deux/{seance_id}/verdict",
  },
  chatDeValidation: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/entrainements/lignes/{ligne_id}/chat",
  },
  poserUneQuestionDeValidation: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/entrainements/lignes/{ligne_id}/chat",
  },
  catalogueDeSujets: { method: "GET", path: "/api/v2/enfants/{child_id}/sujets/catalogue" },
  donnerUnSujet: { method: "POST", path: "/api/v2/enfants/{child_id}/sujets" },
  sujetsDeLEnfant: { method: "GET", path: "/api/v2/enfants/{child_id}/sujets" },
  sujetsAJuger: { method: "GET", path: "/api/v2/enfants/{child_id}/sujets/a-juger" },
  retirerUnSujet: {
    method: "DELETE",
    path: "/api/v2/enfants/{child_id}/sujets/{sujet_donne_id}",
  },
  jugerUnSujet: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/sujets/{sujet_donne_id}/verdict",
  },
  mesSujets: { method: "GET", path: "/api/v2/me/sujets" },
  photographierUnePageDeCopie: {
    method: "POST",
    path: "/api/v2/me/sujets/{sujet_donne_id}/photos",
  },
  retirerUnePageDeCopie: {
    method: "DELETE",
    path: "/api/v2/me/sujets/{sujet_donne_id}/photos/{photo_id}",
  },
  rendreLaCopie: { method: "POST", path: "/api/v2/me/sujets/{sujet_donne_id}/rendre" },
  // Le sujet de bac du dimanche (lot P6) : l'élève choisit l'heure, le parent
  // choisit le sujet, tient le chronomètre et note au barème.
  choisirLHeureDuSujet: { method: "PUT", path: "/api/v2/me/sujets/{sujet_donne_id}/heure" },
  sujetDuDimancheDeLEnfant: { method: "GET", path: "/api/v2/enfants/{child_id}/sujets/dimanche" },
  choisirLeSujetDuDimanche: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/sujets/dimanche",
  },
  demarrerLeChronometre: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/sujets/{sujet_donne_id}/demarrer",
  },
  arreterLeChronometre: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/sujets/{sujet_donne_id}/arreter",
  },
  noterUnSujet: {
    method: "POST",
    path: "/api/v2/enfants/{child_id}/sujets/{sujet_donne_id}/notes",
  },
  // La dictée de la semaine (lot P3) : routes du parent seulement — le texte y
  // est, et aucune route en `/me` ne le rend.
  dicteeDeLaSemaine: { method: "GET", path: "/api/v2/enfants/{child_id}/dictee" },
  donnerLaDictee: { method: "POST", path: "/api/v2/enfants/{child_id}/dictee" },
  carnetsDeLEnfant: { method: "GET", path: "/api/v2/enfants/{child_id}/carnet" },
  preparerLeCarnet: { method: "POST", path: "/api/v2/enfants/{child_id}/carnet" },
  imprimerLeCarnet: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/carnet/{carnet_id}/carnet.pdf",
  },
  imprimerLeCorrigeDuCarnet: {
    method: "GET",
    path: "/api/v2/enfants/{child_id}/carnet/{carnet_id}/corrige.pdf",
  },
  maFicheDuSoir: { method: "GET", path: "/api/v2/me/carnet/ce-soir" },
  photographierMaFiche: { method: "PUT", path: "/api/v2/me/carnet/fiches/{fiche_id}/photo" },
  // Le cahier de la semaine (lot P5) : l'enfant photographie, le parent lit ce
  // qui a été reconnu et retire ce qui est faux.
  monCahierDeLaSemaine: { method: "GET", path: "/api/v2/me/cahier-semaine" },
  photographierMonCahier: { method: "POST", path: "/api/v2/me/cahier-semaine/photos" },
  retirerUnePageDeMonCahier: {
    method: "DELETE",
    path: "/api/v2/me/cahier-semaine/photos/{photo_id}",
  },
  cahierDeLaSemaine: { method: "GET", path: "/api/v2/enfants/{child_id}/cahier-semaine" },
  retirerUneReconnaissanceDuCahier: {
    method: "DELETE",
    path: "/api/v2/enfants/{child_id}/cahier-semaine/reconnaissances/{reconnaissance_id}",
  },
  health:{ method: "GET", path: "/health" },
};

export function buildUrl<N extends OperationName>(
  name: N,
  options: Pick<CallOptions<N>, "params" | "query"> = {},
): string {
  const operation = OPERATIONS[name];
  let path: string = operation.path;
  for (const [key, value] of Object.entries(options.params ?? {})) {
    path = path.replace(`{${key}}`, encodeURIComponent(String(value)));
  }
  if (path.includes("{")) throw new Error(`paramètre manquant pour ${String(name)} : ${path}`);

  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null) query.set(key, String(value));
  }
  const search = query.toString();
  return search ? `${path}?${search}` : path;
}

async function readError(response: Response): Promise<ApiError> {
  let code = "unexpected";
  let message = "Quelque chose n'a pas marché. Réessaie dans un instant.";
  let retryAfter: number | null = null;
  try {
    const payload = (await response.json()) as { detail?: unknown };
    const detail = payload.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const shaped = detail as { code?: string; message?: string; retry_after_seconds?: number };
      code = shaped.code ?? code;
      message = shaped.message ?? message;
      retryAfter = shaped.retry_after_seconds ?? null;
    } else if (Array.isArray(detail)) {
      // FastAPI's own validation shape. Never shown as-is: it is English and
      // written for a developer.
      code = "validation_failed";
      message = "Je n'ai pas compris ce qui a été envoyé. Réessaie.";
    }
  } catch {
    /* an empty or non-JSON body keeps the defaults above */
  }
  return new ApiError(response.status, code, message, retryAfter);
}

export async function call<N extends OperationName>(
  name: N,
  options: CallOptions<N> = {},
): Promise<Operations[N]["response"]> {
  const operation = OPERATIONS[name];
  const url = buildUrl(name, options);

  if (currentShell === "child" && violatesChildPartition(url)) {
    throw new PartitionViolation(url);
  }

  const hasBody = options.body !== undefined;
  // L'attente commence ici et **pas avant** : la violation de partition
  // ci-dessus ne touche pas le réseau, et la compter ferait avancer une barre
  // pour un appel qui n'a jamais eu lieu. Elle se termine dans le `finally`,
  // donc aussi bien sur un refus du serveur que sur une coupure — un segment
  // qui resterait en vol après une erreur figerait la barre pour de bon.
  const attente = demarrer(name);
  try {
    let response: Response;
    try {
      response = await fetch(url, {
        method: operation.method,
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          ...(hasBody ? { "Content-Type": "application/json" } : {}),
        },
        body: hasBody ? JSON.stringify(options.body) : undefined,
        signal: options.signal,
      });
    } catch {
      if (!navigator.onLine) throw offlineError();
      throw new ApiError(0, "network", "La connexion a été interrompue.");
    }

    if (!response.ok) throw await readError(response);
    if (response.status === 204) return undefined as Operations[N]["response"];

    const text = await response.text();
    if (!text) return undefined as Operations[N]["response"];
    return JSON.parse(text) as Operations[N]["response"];
  } finally {
    terminer(attente);
  }
}
